import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, FlaskConical, Lock } from 'lucide-react'

import { cn } from '@/lib/utils.ts'
import { useUserStore } from '@/store/user.ts'
import { activeMembership } from '@/routes/start/lib/activeMembership.ts'
import { ORG_PROJECTS_PATH, START_PATH } from '@/routes/start/lib/startPaths.ts'
import { useProjectsStore } from '@/routes/settings/projects/lib/stores/useProjectsStore.ts'
import { resolveLicenceBanner } from '@/lib/licence/licenceBanner.ts'
import { formatLicenceDate } from '@/lib/licence/formatLicenceDate.ts'
import { useLicenceClock, useStageLicence } from '@/lib/licence/useStageLicence.ts'
import { useLicenceContact } from '@/lib/licence/useLicenceContact.ts'

export const LICENCE_KEY_PATH = '/settings/organization/information'

/**
 * The licence and demo message at the top of the content (docs/plans/model-licencji.md,
 * "Klient: co widzi użytkownik"). In the page flow rather than in the fixed banner stack:
 * it is a state of the company or the project, not of the connection, and it should scroll
 * away with the page instead of permanently taking a row of a phone screen.
 */
export default function LicenceBanner() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const setProjectDialogOpened = useProjectsStore(s => s.setDialogOpened)
  const user = useUserStore(s => s.user)
  const stageProject = useStageLicence()
  const now = useLicenceClock(stageProject?.demo?.expiresAt)
  const roleCode = activeMembership(user)?.role?.code
  const banner = resolveLicenceBanner({
    stageProject,
    userLicence: user?.licence,
    isOrganizationAdmin:
      roleCode === 'ROLE_ORGANIZATION_ADMIN' || roleCode === 'ROLE_ORGANIZATION_OWNER',
    projectsCount: user?.projectsCount,
    now,
  })
  const needsContact = banner?.kind === 'licenceExpired' || banner?.kind === 'licenceExpiring'
  const { data: contact } = useLicenceContact(needsContact)

  if (!banner) return null

  const language = i18n.resolvedLanguage
  const isBlocking = banner.kind === 'licenceExpired' || banner.kind === 'demoExpired'
  const Icon = banner.kind === 'demo' ? FlaskConical : isBlocking ? Lock : AlertTriangle

  let text: string
  switch (banner.kind) {
    case 'licenceExpired':
      text = t('licence.banner.expired')
      break
    case 'licenceExpiring':
      text = t('licence.banner.expiring', { date: formatLicenceDate(banner.expiresAt, language) })
      break
    case 'demo':
      text =
        banner.issueLimit != null
          ? t('licence.banner.demoWithLimit', {
              date: formatLicenceDate(banner.expiresAt, language),
              limit: banner.issueLimit,
            })
          : t('licence.banner.demo', { date: formatLicenceDate(banner.expiresAt, language) })
      break
    case 'demoExpired':
      text = t('licence.banner.demoExpired')
      break
  }

  const contactParts = needsContact
    ? [
        contact?.email && t('licence.contact.email', { email: contact.email }),
        contact?.phone && t('licence.contact.phone', { phone: contact.phone }),
      ].filter(Boolean)
    : []

  return (
    <div
      role="status"
      data-testid="licence-banner"
      data-kind={banner.kind}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm',
        isBlocking &&
          'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-100',
        banner.kind === 'licenceExpiring' &&
          'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-100',
        banner.kind === 'demo' &&
          'border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/50 dark:text-sky-100',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        {text}
        {contactParts.length > 0 && <> {contactParts.join(' ')}</>}
      </span>
      {banner.kind === 'licenceExpiring' && banner.showKeyLink && (
        <Link to={LICENCE_KEY_PATH} className="shrink-0 font-medium underline underline-offset-2">
          {t('licence.banner.enterKey')}
        </Link>
      )}
      {(banner.kind === 'demo' || banner.kind === 'demoExpired') &&
        banner.showCreateProject &&
        (banner.createProjectInWizard ? (
          <Link
            to={START_PATH}
            data-testid="licence-banner-create-project"
            className="shrink-0 font-medium underline underline-offset-2"
          >
            {t('licence.banner.createOwnProject')}
          </Link>
        ) : (
          // The company already has a project: the ordinary new-project form, which the
          // project list opens from the store flag (D7).
          <button
            type="button"
            data-testid="licence-banner-create-project"
            onClick={() => {
              setProjectDialogOpened(true)
              navigate(ORG_PROJECTS_PATH)
            }}
            className="shrink-0 font-medium underline underline-offset-2"
          >
            {t('licence.banner.createOwnProject')}
          </button>
        ))}
    </div>
  )
}
